/**
 * Task hierarchy utilities for sorting tasks into depth-first parent-child order.
 *
 * @see PlanTasksTimelineView - primary consumer for timeline (Gantt) rendering
 */ // Models
/**
 * Sorts tasks into depth-first hierarchical order where each parent appears immediately
 * before its children. Orphans (tasks whose parent is not in the input list) are treated as
 * roots, so their own subtrees still expand; tasks trapped in a cycle are appended at depth 0.
 *
 * @param tasks The flat list of tasks to sort.
 * @param getChildren Resolver returning a parent's direct children — e.g. a store index
 * (`taskStore.getChildTasks`) in the grid, or an in-memory index over the input list. Resolved
 * children are scoped to `tasks`; roots always come from `tasks`.
 * @returns Sorted tasks and a depth map.
 */ export function sortTasksInHierarchicalOrder(tasks, getChildren) {
    const sortedTasks = [];
    const depthMap = new Map();
    // Task ids in scope, used to keep resolved children within the input set.
    const inputIds = new Set(tasks.map((t)=>t.id));
    // Roots: tasks with no parent, plus orphans (parent not in the list) so their subtrees still expand.
    const roots = tasks.filter((task)=>task.parentTaskId == null || !inputIds.has(task.parentTaskId));
    // Direct children of a parent id, scoped to the input set.
    const resolveChildren = (parentId)=>getChildren(parentId).filter((task)=>inputIds.has(task.id));
    // Depth-first walk from roots; the depthMap guard prevents duplicate placement and cycles.
    const walk = (siblings, depth)=>{
        for (const task of siblings){
            if (depthMap.has(task.id)) {
                continue;
            }
            depthMap.set(task.id, depth);
            sortedTasks.push(task);
            walk(resolveChildren(task.id), depth + 1);
        }
    };
    walk(roots, 0);
    // Safety net: append any task still unplaced (trapped in a cycle) at depth 0 so every task appears.
    if (sortedTasks.length < tasks.length) {
        const seen = new Set(sortedTasks.map((t)=>t.id));
        for (const task of tasks){
            if (!seen.has(task.id)) {
                depthMap.set(task.id, 0);
                sortedTasks.push(task);
            }
        }
    }
    return {
        sortedTasks,
        depthMap
    };
}
